import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { proposalApi } from '@/api/commercial/proposals';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormError } from '@/components/ui/form-error';
import { useFormError } from '@/hooks/use-form-error';
import { toast } from '@/hooks/use-toast';
import { getErrorMessage } from '@/lib/error-message';

interface ResumeCancelDialogProps {
  open: boolean;
  onClose: () => void;
  proposalId?: string;
  onSuccess?: () => void;
}

export function ResumeCancelDialog({
  open,
  onClose,
  proposalId,
  onSuccess,
}: ResumeCancelDialogProps) {
  const queryClient = useQueryClient();
  const { formError, showFormError, clearFormError } = useFormError(open);

  const cancelMutation = useMutation({
    mutationFn: () => {
      if (!proposalId) throw new Error('Proposal ID is required');
      return proposalApi.cancel(proposalId);
    },
    onSuccess: () => {
      clearFormError();
      toast({
        variant: 'success',
        title: 'Proposta cancelada',
        description: 'A proposta foi cancelada com sucesso.',
      });

      if (proposalId) {
        queryClient.invalidateQueries({ queryKey: ['proposal', proposalId] });
        queryClient.invalidateQueries({ queryKey: ['proposals'] });
      }

      onSuccess?.();
      onClose();
    },
    onError: (error) => {
      showFormError(error);
      toast({
        title: 'Erro ao cancelar proposta',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    },
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[85vh] w-[calc(100vw-2rem)] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Cancelar Proposta</DialogTitle>
          <DialogDescription>Esta ação não pode ser desfeita.</DialogDescription>
        </DialogHeader>

        <p className="text-sm text-gray-700 py-2">
          Tem certeza que deseja cancelar esta proposta? Após o cancelamento, não será possível
          reabri-la.
        </p>

        <FormError error={formError} onDismiss={clearFormError} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={cancelMutation.isPending}>
            Voltar
          </Button>
          <Button
            variant="destructive"
            onClick={() => cancelMutation.mutate()}
            disabled={cancelMutation.isPending}
          >
            {cancelMutation.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            {cancelMutation.isPending ? 'Cancelando...' : 'Cancelar proposta'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
